import { useEffect, useId, useRef, useState, type ReactNode } from "react";
import { ChevronDown, Plus, X } from "lucide-react";
import type { EventOptions, EventPart, ScoreTypeOption, SpaceEvent } from "@repo/shared-types";
import {
  LONGEST,
  RANKINGS,
  SHEET_PREFIX,
  checkPartDates,
  fieldsOf,
  isPost,
  partLabel,
  rankingLabel,
  readEventDates,
  timeLimitRule,
  wallOf,
  type PartBounds,
  type PartFields,
  type PublishChoice,
  type Ranking,
  type Read,
} from "@/lib/event-form";
import { CogMenu, MenuButton } from "@/components/cog-menu";
import { ErrorNote, ProblemNote, ProblemProvider, SegmentedControl, ToggleChips, useFormProblem, useProblem } from "@/components/form";
import { Modal } from "@/components/modal";
import { Button } from "@/components/ui/button";
import { Card, CardAction, CardContent, CardHeader } from "@/components/ui/card";
import { Field, FieldDescription, FieldError, FieldLabel, FieldLegend, FieldSet } from "@/components/ui/field";
import { Input } from "@/components/ui/input";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { InputGroup, InputGroupAddon, InputGroupButton, InputGroupInput } from "@/components/ui/input-group";
import { NativeSelect, NativeSelectOption } from "@/components/ui/native-select";
import { Textarea } from "@/components/ui/textarea";
import { Toggle } from "@/components/ui/toggle";
import {
  LENGTH_UNITS,
  formatMoment,
  fromWallTime,
  joinWall,
  lengthBetween,
  splitWall,
  toWallTime,
  unitLabel,
  wallAfter,
  type LengthUnit,
} from "@/lib/times";

/**
 * The fields the event forms share. Uncontrolled, like every form in this app:
 * the router's action reads them. The state here is which inputs to show —
 * which follows the score type somebody picked, and the optional boxes they
 * opened — and the start, which the end is worked out from and Later's boxes
 * open on.
 *
 * The buttons here are outlined and as tall as the boxes: they sit in rows of
 * them, whose bottoms they line up with.
 */

/**
 * An optional moment, behind "+ Add …" until there is one. The box opens
 * focused, already open when there is a value, and × closes it, which sends no
 * box at all. A form that reads a missing box as "leave it as it is" passes
 * `clears`, so that closing it sends it empty instead and takes the moment
 * away.
 */
export function OptionalTime({
  name,
  label,
  add,
  remove,
  initial = "",
  hint,
  clears = false,
  addClassName = "",
  min,
  max,
  fixed = false,
  onValue,
}: {
  name: string;
  label: string;
  /** The button's words: "+ Add a deadline". */
  add: string;
  /** The ×'s name for a screen reader: "Remove the deadline". */
  remove: string;
  /** What the box shows when it opens: a wall time, or "" for none. */
  initial?: string;
  hint?: ReactNode;
  clears?: boolean;
  /** Where the button sits among its neighbours. */
  addClassName?: string;
  /** The earliest and latest wall times its picker offers. */
  min?: string;
  max?: string;
  /** Open with no ×: a moment that may move but not go. */
  fixed?: boolean;
  /** Told what the box holds as it changes, "" once it is closed. */
  onValue?: (value: string) => void;
}) {
  const id = useId();
  const [open, setOpen] = useState(initial !== "" || fixed);
  const problem = useProblem(name);
  if (!open) {
    return (
      <>
        {clears && <input type="hidden" name={name} value="" />}
        <Button
          type="button"
          variant="outline"
          onClick={() => {
            setOpen(true);
            onValue?.(initial);
          }}
          className={addClassName}
        >
          {add}
        </Button>
      </>
    );
  }
  return (
    <Field data-invalid={problem ? true : undefined}>
      <FieldLabel htmlFor={id}>{label}</FieldLabel>
      <div className="flex items-center gap-2">
        <Input
          id={id}
          name={name}
          type="datetime-local"
          defaultValue={initial}
          autoFocus={initial === "" && !fixed}
          min={min || undefined}
          max={max || undefined}
          onChange={onValue && ((change) => onValue(change.target.value))}
          aria-invalid={problem ? true : undefined}
          aria-describedby={hint ? `${id}-hint` : undefined}
          className="min-w-0 flex-1"
        />
        {!fixed && (
          <Button
            type="button"
            variant="outline"
            size="icon"
            aria-label={remove}
            onClick={() => {
              setOpen(false);
              onValue?.("");
            }}
          >
            <X aria-hidden="true" />
          </Button>
        )}
      </div>
      {problem && <FieldError>{problem}</FieldError>}
      {hint && <FieldDescription id={`${id}-hint`}>{hint}</FieldDescription>}
    </Field>
  );
}

/**
 * A moment as two of the browser's own boxes, a date and a time, side by side
 * — each opens the phone's own picker — named `${name}.date` and
 * `${name}.time`, which `wallOf` joins again. The date box is `id`, for a
 * label; the time box is named "`label` time" for a screen reader.
 */
function DateTimeBoxes({
  id,
  name,
  label,
  initial,
  onValue,
  invalid,
  autoFocus,
  labelled = false,
}: {
  id: string;
  name: string;
  /** What the pair is called: "Starts". */
  label: string;
  /** A wall time, or "" for empty boxes. */
  initial: string;
  /** Told the wall time as either box changes, "" while one is empty. */
  onValue?: (wall: string) => void;
  invalid?: boolean;
  autoFocus?: boolean;
  /** Whether a `FieldLabel` names the date box; otherwise it is named `label` itself. */
  labelled?: boolean;
}) {
  const [boxes, setBoxes] = useState(() => splitWall(initial));
  const change = (next: { date: string; time: string }) => {
    setBoxes(next);
    onValue?.(joinWall(next.date, next.time) ?? "");
  };
  return (
    <div className="flex items-center gap-2">
      <Input
        id={id}
        name={`${name}.date`}
        type="date"
        aria-label={labelled ? undefined : label}
        value={boxes.date}
        onChange={(event) => change({ ...boxes, date: event.target.value })}
        autoFocus={autoFocus}
        aria-invalid={invalid || undefined}
        className="min-w-0 flex-1"
      />
      <Input
        name={`${name}.time`}
        type="time"
        aria-label={`${label} time`}
        value={boxes.time}
        onChange={(event) => change({ ...boxes, time: event.target.value })}
        aria-invalid={invalid || undefined}
        className="w-28 shrink-0"
      />
    </div>
  );
}

/** How an event's end opens: a length after its start where it is one (0088), its own moment otherwise. */
function endsOpening(event: SpaceEvent | undefined, zone: string) {
  const whole = event?.endsAt ? lengthBetween(event.startsAt, event.endsAt, zone) : null;
  // A length the box could not hold — 1,530 minutes — is a moment of its own too.
  const length = whole && whole.amount <= LONGEST ? whole : null;
  if (event?.endsAt && !length) return { custom: true, amount: "1", unit: "days" as LengthUnit, wall: toWallTime(event.endsAt, zone) };
  return { custom: false, amount: String(length?.amount ?? 1), unit: length?.unit ?? "days", wall: "" };
}

/**
 * When the event ends (0088): a length after Starts — a number and its unit
 * in one box, worked out again whenever Starts moves — with the moment it
 * comes to under it, or with Custom pressed a date and time of its own. Always
 * there: the form saves no event without an end. Named under `endsAt.`, which
 * `readEventDates` reads; the end in either form is its deadline (0083).
 */
function EventEnds({
  starts,
  zone,
  event,
  onValue,
}: {
  /** Starts' wall time, "" while it is not one. */
  starts: string;
  zone: string;
  event?: SpaceEvent;
  /** Told the end's wall time as it changes, "" while it is not one. */
  onValue?: (wall: string) => void;
}) {
  const id = useId();
  const [opening] = useState(() => endsOpening(event, zone));
  const [custom, setCustom] = useState(opening.custom);
  const [amount, setAmount] = useState(opening.amount);
  const [unit, setUnit] = useState<LengthUnit>(opening.unit);
  // Custom's boxes open on wherever the length came to, keyed so they do.
  const [own, setOwn] = useState({ wall: opening.wall, key: 0 });
  const problem = useProblem("endsAt");

  const count = /^\d+$/.test(amount) ? Number(amount) : NaN;
  // The unit reads after the number: "1 day", "3 days" — and "days" while there is none.
  const shown = Number.isNaN(count) ? 2 : count;
  const after = starts && count >= 1 && count <= LONGEST ? wallAfter(starts, count, unit, zone) : null;
  const end = custom ? own.wall : (after ?? "");
  const told = useRef<string | null>(null);
  useEffect(() => {
    if (told.current === end) return;
    told.current = end;
    onValue?.(end);
  }, [end, onValue]);
  const moment = !custom && after ? fromWallTime(after, zone) : null;

  return (
    <Field data-invalid={problem ? true : undefined}>
      <FieldLabel htmlFor={id}>Ends</FieldLabel>
      {/* Custom's date and time need most of a phone's row: Custom wraps under them there. */}
      <div className="flex flex-wrap items-center gap-2">
        {custom ? (
          <div className="min-w-[16.5rem] flex-1">
            <DateTimeBoxes
              key={own.key}
              id={id}
              name="endsAt"
              label="Ends"
              labelled
              initial={own.wall}
              onValue={(wall) => setOwn((kept) => ({ ...kept, wall }))}
              invalid={Boolean(problem)}
            />
          </div>
        ) : (
          // The unit is shadcn's input-group dropdown: a button in the box
          // opening a menu, which the box's addon leaves its clicks to.
          <InputGroup className="w-44">
            <InputGroupInput
              id={id}
              name="endsAt.amount"
              inputMode="numeric"
              value={amount}
              onChange={(change) => setAmount(change.target.value.replace(/\D/g, ""))}
              maxLength={String(LONGEST).length}
              aria-invalid={problem ? true : undefined}
              aria-describedby={moment ? `${id}-at` : undefined}
            />
            <InputGroupAddon align="inline-end">
              <DropdownMenu modal={false}>
                <DropdownMenuTrigger asChild>
                  <InputGroupButton size="sm" aria-label={`Unit: ${unitLabel(unit, shown)}`}>
                    {unitLabel(unit, shown)}
                    <ChevronDown aria-hidden="true" />
                  </InputGroupButton>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuRadioGroup value={unit} onValueChange={(next) => setUnit(next as LengthUnit)}>
                    {LENGTH_UNITS.map((option) => (
                      <DropdownMenuRadioItem key={option} value={option}>
                        {unitLabel(option, shown)}
                      </DropdownMenuRadioItem>
                    ))}
                  </DropdownMenuRadioGroup>
                </DropdownMenuContent>
              </DropdownMenu>
              <input type="hidden" name="endsAt.unit" value={unit} />
            </InputGroupAddon>
          </InputGroup>
        )}
        {custom && <input type="hidden" name="endsAt.custom" value="on" />}
        <Toggle
          variant="outline"
          pressed={custom}
          onPressedChange={(pressed) => {
            if (pressed) setOwn((kept) => ({ wall: after ?? kept.wall, key: kept.key + 1 }));
            setCustom(pressed);
          }}
          className="shrink-0"
        >
          Custom
        </Toggle>
      </div>
      {problem && <FieldError>{problem}</FieldError>}
      {moment && (
        <FieldDescription id={`${id}-at`}>{formatMoment(moment, zone)}</FieldDescription>
      )}
    </Field>
  );
}

/**
 * What a post goes without (0090): its sport, its dates, its ranking and its
 * parts. Hidden while the format is Post, and disabled, so the form sends
 * none of it; kept as typed, so switching back finds it as it was.
 */
export function NotOnAPost({ post, children }: { post: boolean; children: ReactNode }) {
  return (
    <fieldset disabled={post} hidden={post} className="min-w-0">
      {children}
    </fieldset>
  );
}

export function EventFields({
  options,
  zone,
  event,
  image,
  onDeadline,
  onPost,
}: {
  options: EventOptions;
  zone: string;
  event?: SpaceEvent;
  /** The image's field, while pictures are on: with the rest of what the event says, before Publish. */
  image?: ReactNode;
  /** Told what the Deadline box holds as it changes, "" once it is closed. */
  onDeadline?: (value: string) => void;
  /** Told whether the format chosen is Post, as it changes: New event hides the parts by it. */
  onPost?: (post: boolean) => void;
}) {
  const id = useId();
  const wall = (iso: string | null | undefined) => (iso ? toWallTime(iso, zone) : "");
  // A new event starts now unless somebody says otherwise; read once, so a
  // re-render never moves what the box shows.
  const [starts, setStarts] = useState(() => wall(event ? event.startsAt : new Date().toISOString()));
  // So does the description, the way a result's note does: once open it stays,
  // so nothing typed is thrown away, and emptying it clears it. Each part has a
  // description of its own, where the workout goes.
  const [describing, setDescribing] = useState(Boolean(event?.description));
  const current = event?.status ?? "draft";
  const [publish, setPublish] = useState<PublishChoice>(
    current === "published" ? "now" : current === "scheduled" ? "at" : "draft",
  );
  // Later's box opens on whatever Starts says when Later is tapped, focused; a
  // scheduled event's opens on its own time.
  const [later, setLater] = useState({ at: current === "scheduled" ? wall(event?.publishedAt) : "", tapped: false });
  const choosePublish = (choice: PublishChoice) => {
    if (choice === "at") setLater({ at: starts, tapped: true });
    setPublish(choice);
  };
  const [formatId, setFormatId] = useState(event?.formatId ?? options.formats[0]?.id ?? "");
  const [ranking, setRanking] = useState<Ranking>("none");
  const allowsOverall = options.formats.find((format) => format.id === formatId)?.allowsOverall ?? false;
  const post = isPost(options, formatId);
  // An event ranked overall keeps its ranking, so only a format that allows
  // one can be chosen for it; and a saved event never changes into or out of
  // a post (0090), so its edit leaves Post out — and a post's shows no format.
  const rankedOverall = event !== undefined && event.ranking !== "none";
  const formats = options.formats.filter(
    (format) => (!rankedOverall || format.allowsOverall) && (event === undefined || !format.isPost),
  );
  const chooseFormat = (next: string) => {
    setFormatId(next);
    onPost?.(isPost(options, next));
  };
  const titleProblem = useProblem("title");
  const startsProblem = useProblem("startsAt");
  const publishProblem = useProblem("publishAt");

  return (
    <div className="space-y-6">
      <Field data-invalid={titleProblem ? true : undefined}>
        <FieldLabel htmlFor={`${id}-title`}>Title</FieldLabel>
        <Input
          id={`${id}-title`}
          name="title"
          required
          maxLength={120}
          defaultValue={event?.title}
          aria-invalid={titleProblem ? true : undefined}
        />
        {titleProblem && <FieldError>{titleProblem}</FieldError>}
      </Field>

      {event && post ? (
        // A post stays one (0090): its format is sent as it is, and not offered.
        <input type="hidden" name="formatId" value={formatId} />
      ) : (
        <div className="grid gap-6 sm:grid-cols-2 sm:gap-4">
          <Field>
            <FieldLabel htmlFor={`${id}-format`}>Format</FieldLabel>
            <NativeSelect
              id={`${id}-format`}
              name="formatId"
              value={formatId}
              onChange={(change) => chooseFormat(change.target.value)}
              className="w-full"
            >
              {formats.map((format) => (
                <NativeSelectOption key={format.id} value={format.id}>
                  {format.label}
                </NativeSelectOption>
              ))}
            </NativeSelect>
          </Field>
          <NotOnAPost post={post}>
            <Field>
              <FieldLabel htmlFor={`${id}-sport`}>Sport</FieldLabel>
              <NativeSelect
                id={`${id}-sport`}
                name="sportId"
                defaultValue={event?.sportId ?? options.sports[0]?.id}
                className="w-full"
              >
                {options.sports.map((sport) => (
                  <NativeSelectOption key={sport.id} value={sport.id}>
                    {sport.label}
                  </NativeSelectOption>
                ))}
              </NativeSelect>
            </Field>
          </NotOnAPost>
        </div>
      )}

      <NotOnAPost post={post}>
        <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 sm:gap-4">
          <Field data-invalid={startsProblem ? true : undefined}>
            <FieldLabel htmlFor={`${id}-starts`}>Starts</FieldLabel>
            <DateTimeBoxes
              id={`${id}-starts`}
              name="startsAt"
              label="Starts"
              labelled
              initial={starts}
              onValue={setStarts}
              invalid={Boolean(startsProblem)}
            />
            {startsProblem && <FieldError>{startsProblem}</FieldError>}
          </Field>
          {/* The event's end is its deadline (0083), still endsAt on the wire. */}
          <EventEnds starts={starts} zone={zone} event={event} onValue={onDeadline} />
        </div>
      </NotOnAPost>

      {post ? null : event ? (
        event.ranking !== "none" && (
          <p className="text-sm text-muted-foreground">
            Overall ranking: {rankingLabel(event.ranking)}. It cannot change once the event is posted.
          </p>
        )
      ) : (
        allowsOverall && (
          <div className="space-y-2">
            <SegmentedControl
              label="Overall ranking"
              name="ranking"
              options={RANKINGS.map(({ value, label }) => ({ value, label }))}
              value={ranking}
              onChange={setRanking}
            />
            <FieldDescription>
              {rankingLabel(ranking)}: {RANKINGS.find((option) => option.value === ranking)?.hint}. Only parts
              with a leaderboard count. Athletes choose one level for the whole event when it has an overall
              ranking. It cannot change once the event is posted.
            </FieldDescription>
          </div>
        )
      )}

      {describing ? (
        <Field>
          <FieldLabel htmlFor={`${id}-description`}>Description</FieldLabel>
          <Textarea
            id={`${id}-description`}
            name="description"
            rows={3}
            maxLength={10_000}
            defaultValue={event?.description ?? ""}
            autoFocus={!event?.description}
          />
        </Field>
      ) : (
        <Button type="button" variant="outline" onClick={() => setDescribing(true)}>
          + Add a description
        </Button>
      )}

      {image}

      <div className="space-y-2">
        <SegmentedControl
          label="Publish"
          name="publish"
          options={[
            { value: "now", label: current === "published" ? "Published" : "Now" },
            { value: "at", label: "Later" },
            { value: "draft", label: "Draft" },
          ]}
          value={publish}
          onChange={choosePublish}
        />
        {publish === "at" && (
          <div className="sm:max-w-80">
            <DateTimeBoxes
              id={`${id}-publish-at`}
              name="publishAt"
              label="Publish at"
              initial={later.at}
              autoFocus={later.tapped}
              invalid={Boolean(publishProblem)}
            />
          </div>
        )}
        {publishProblem && <FieldError>{publishProblem}</FieldError>}
        {publish === "now" && current === "published" && event?.publishedAt && (
          <FieldDescription>Published {formatMoment(event.publishedAt, zone)}</FieldDescription>
        )}
      </div>
    </div>
  );
}

// One score type picked for a part, by id; the key keeps React's rows apart.
type ScoreRow = { key: number; type: string };

/** Renders a part's result under how it is scored, for the types chosen: the log's form. */
export type PartResultRender = (
  types: ScoreTypeOption[],
  timeLimit: "required" | "optional" | "none",
) => ReactNode;

/** The log's result boxes for the part at `index`, starting from what its sheet sent before. */
export type PartResultFor = (initial: PartFields | undefined, index: number) => PartResultRender;

/**
 * One part: its title, start and description, and how it is scored — its
 * score types in tiebreak order, a time limit when they take one, whether its
 * results are split into levels, and whether it has a leaderboard at all.
 * Named `${prefix}.title`, `${prefix}.timeLimit.min` and `.sec`,
 * `${prefix}.levels`, `${prefix}.leaderboard`, `${prefix}.scores.0.type` and
 * so on, which is what readParts reads back. Drawn in a part's sheet
 * (`PartSheet`), which says which part it is; `initial` is what it sent
 * before, when it opens again to be changed.
 *
 * What each type allows comes from the list the api sent: whether it ranks at
 * all, and whether its part takes a time limit. Nothing here knows a type by
 * name.
 *
 * For a workout of your own there is no start and no leaderboard, and the
 * part's result is typed right under how it is scored: `result` renders it
 * for the types chosen, whose number decides its boxes.
 */
export function PartEditor({
  options,
  prefix,
  label,
  initial,
  result,
  bounds,
}: {
  options: EventOptions;
  prefix: string;
  label: string;
  initial?: PartFields;
  result?: PartResultRender;
  /** The event's own dates, which a part's live inside. */
  bounds?: PartBounds;
}) {
  const id = useId();
  const first = options.scoreTypes[0]?.id ?? "";
  const option = (id: string) => options.scoreTypes.find((candidate) => candidate.id === id);
  // A part that is not scored has nothing to break a tie with.
  const ranks = (type: string) => option(type)?.measure !== "none";

  const typed = (name: string) => initial?.find(([field]) => field === name)?.[1] ?? "";
  const ticked = (name: string) => initial?.some(([field]) => field === name) ?? false;
  const typedScores = (initial ?? []).filter(([field]) => /^scores\.\d+\.type$/.test(field)).map(([, type]) => type);
  const [scores, setScores] = useState<ScoreRow[]>(() =>
    typedScores.length > 0 ? typedScores.map((type, key) => ({ key, type })) : [{ key: 0, type: first }],
  );
  const [nextKey, setNextKey] = useState(() => Math.max(typedScores.length, 1));
  // Its chips start as they were saved; a part saved unscored sent none, and
  // starts as a new one does should it become scored.
  const chipsSaved = initial !== undefined && ranks(typedScores[0] ?? "");
  // A deadline and verification are about a leaderboard, so they are offered
  // only while the part has one.
  const [ranked, setRanked] = useState(chipsSaved ? ticked("leaderboard") : true);
  const [levels, setLevels] = useState(chipsSaved ? ticked("levels") : true);
  const [team, setTeam] = useState(ticked("team"));
  const [verifiable, setVerifiable] = useState(ticked("verifiable"));
  // While the event has a deadline, a part with a board has one at or before
  // it, never earlier than the part starts (0083): a new part starts on the
  // event's, and its box has no × to take it away.
  const [partStarts, setPartStarts] = useState(typed("startsAt"));
  const eventDeadline = bounds?.deadline ?? "";
  const deadline = initial === undefined ? eventDeadline : typed("deadline");

  const titleProblem = useProblem(`${prefix}.title`);
  const limitProblem = useProblem(`${prefix}.timeLimit`);
  const leading = option(scores[0]?.type ?? "");
  const limit = timeLimitRule(
    scores.map((row) => row.type),
    options,
  );

  const setType = (key: number, type: string) =>
    setScores((rows) => {
      const changed = rows.map((row) => (row.key === key ? { ...row, type } : row));
      return ranks(changed[0]?.type ?? "") ? changed : changed.slice(0, 1);
    });

  return (
    <div className="space-y-5">
      <div className="grid gap-5 sm:grid-cols-2 sm:gap-4">
        <Field data-invalid={titleProblem ? true : undefined}>
          <FieldLabel htmlFor={`${id}-title`}>Title</FieldLabel>
          <Input
            id={`${id}-title`}
            name={`${prefix}.title`}
            required
            maxLength={120}
            defaultValue={typed("title")}
            autoFocus={initial === undefined}
            aria-invalid={titleProblem ? true : undefined}
          />
          {titleProblem && <FieldError>{titleProblem}</FieldError>}
        </Field>
        {/* Without one, the part starts with the event. */}
        {!result && (
          <OptionalTime
            name={`${prefix}.startsAt`}
            label="Starts"
            add="+ Add a start time"
            remove="Remove the start time"
            initial={typed("startsAt")}
            addClassName="justify-self-start sm:self-end"
            onValue={setPartStarts}
          />
        )}
      </div>
      <Field>
        <FieldLabel htmlFor={`${id}-description`}>Description</FieldLabel>
        <Textarea
          id={`${id}-description`}
          name={`${prefix}.description`}
          rows={2}
          maxLength={10_000}
          defaultValue={typed("description")}
        />
      </Field>

      <div className="flex flex-wrap items-end gap-2">
        {scores.map((row, index) => (
          <div key={row.key} className="flex min-w-0 items-end gap-2">
            <Field className="w-auto min-w-0">
              <FieldLabel htmlFor={`${id}-score-${row.key}`}>{index === 0 ? "Scored by" : "Then by"}</FieldLabel>
              <NativeSelect
                id={`${id}-score-${row.key}`}
                name={`${prefix}.scores.${index}.type`}
                value={row.type}
                onChange={(change) => setType(row.key, change.target.value)}
              >
                {options.scoreTypes
                  .filter((type) => index === 0 || type.measure !== "none")
                  .map((type) => (
                    <NativeSelectOption key={type.id} value={type.id}>
                      {type.label}
                    </NativeSelectOption>
                  ))}
              </NativeSelect>
            </Field>
            {index > 0 && (
              <Button
                type="button"
                variant="outline"
                size="icon"
                aria-label="Remove this tiebreak"
                onClick={() => setScores((rows) => rows.filter((other) => other.key !== row.key))}
              >
                <X aria-hidden="true" />
              </Button>
            )}
          </div>
        ))}
        <ProblemNote name={`${prefix}.scores`} />
        {ranks(scores[0]?.type ?? "") && scores.length < 5 && (
          <Button
            type="button"
            variant="outline"
            onClick={() => {
              setScores((rows) => [...rows, { key: nextKey, type: first }]);
              setNextKey((key) => key + 1);
            }}
          >
            + Add a tiebreak
          </Button>
        )}
      </div>

      {limit !== "none" && (
        <FieldSet className="gap-0" data-invalid={limitProblem ? true : undefined}>
          {/* Red when refused, as a single box's label is. */}
          <FieldLegend variant="label" className={limitProblem ? "text-destructive" : undefined}>
            {leading?.measure === "time" && leading.better === "lower" ? "Time cap" : "Time limit"}
            {/* The space outside the quieter words, or a screen reader runs them together. */}
            {limit === "optional" && (
              <>
                {" "}
                <span className="font-normal text-muted-foreground">(optional)</span>
              </>
            )}
          </FieldLegend>
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Input
              name={`${prefix}.timeLimit.min`}
              inputMode="numeric"
              aria-label="Minutes"
              placeholder="12"
              defaultValue={typed("timeLimit.min")}
              aria-invalid={limitProblem ? true : undefined}
              className="w-16"
            />
            min
            <Input
              name={`${prefix}.timeLimit.sec`}
              inputMode="numeric"
              aria-label="Seconds"
              placeholder="00"
              defaultValue={typed("timeLimit.sec")}
              aria-invalid={limitProblem ? true : undefined}
              className="w-16"
            />
            sec
          </div>
          {limitProblem && <FieldError className="mt-2">{limitProblem}</FieldError>}
        </FieldSet>
      )}

      {result &&
        result(
          scores.map((row) => option(row.type)).filter((type): type is ScoreTypeOption => type !== undefined),
          limit,
        )}

      {!result && ranks(scores[0]?.type ?? "") && (
        <div className="space-y-5">
          <ToggleChips
            label={`Part ${label} options`}
            options={[
              {
                name: `${prefix}.leaderboard`,
                label: "Leaderboard",
                says: "Rank people on this part",
                on: ranked,
                onChange: setRanked,
              },
              { name: `${prefix}.levels`, label: "Levels", says: "Rx and Scaled levels", on: levels, onChange: setLevels },
              // In an event ranked overall too (0037): a team counts for each
              // of its people.
              { name: `${prefix}.team`, label: "Team part", says: "Team part enabled", on: team, onChange: setTeam },
              ...(ranked
                ? [
                    {
                      name: `${prefix}.verifiable`,
                      label: "Verifiable",
                      says: "Judges verify this part",
                      on: verifiable,
                      onChange: setVerifiable,
                    },
                  ]
                : []),
            ]}
          />
          {ranked && (
            <OptionalTime
              name={`${prefix}.deadline`}
              label="Deadline"
              add="+ Add a deadline"
              remove="Remove the deadline"
              initial={deadline}
              min={partStarts || bounds?.starts}
              max={eventDeadline}
              fixed={eventDeadline !== ""}
              hint={
                eventDeadline
                  ? "A result changed or shared after it stays off the leaderboard until it is verified. It can move earlier, no later than the event's deadline."
                  : "A result changed or shared after it stays off the leaderboard until it is verified. It can move later, never back into the past."
              }
            />
          )}
        </div>
      )}
    </div>
  );
}

/**
 * A part, made in a sheet wherever one is made (decided with Tiago 20
 * October): "New part C" over the event's page, the New event form and the
 * log's form, and "Edit part C" for one kept on a form until it is sent. Its
 * own `<form>`, named under `parts.0`, read by `check` — as the page will
 * read it — when its button is pressed, so a mistake is said here; then
 * handed back by `onSave`. A sheet that sends the part itself says `busy`
 * while it does, and the action's `error`.
 */
export function PartSheet({
  title,
  submit,
  options,
  index,
  initial,
  check,
  onSave,
  onClose,
  busy = false,
  sent,
  result,
  bounds,
}: {
  title: string;
  /** The button's words: "Add part", "Save part". */
  submit: string;
  options: EventOptions;
  /** The part's place among the event's: its letter. */
  index: number;
  initial?: PartFields;
  /** Reads what was typed as the page will: why not, and which box, when it does not read. */
  check: (form: FormData) => Read<unknown>;
  onSave: (fields: PartFields) => void;
  onClose: () => void;
  busy?: boolean;
  /** What the page's action said back, for a sheet that sends the part itself. */
  sent?: { error?: string; field?: string };
  result?: PartResultFor;
  /** The event's dates, for a part of an event: its own are kept inside them. */
  bounds?: PartBounds;
}) {
  const form = useRef<HTMLFormElement>(null);
  // What `check` refused, until the next press: the box it names is outlined.
  const [checked, setChecked] = useState<Read<unknown> | null>(null);
  const shown = useFormProblem(checked ?? sent, form);
  return (
    <Modal title={title} onClose={onClose}>
      <form
        ref={form}
        className="space-y-5"
        // The app says what is missing, beside its box, rather than the browser.
        noValidate
        onChange={shown.onChange}
        onSubmit={(event) => {
          event.preventDefault();
          // The sheet is drawn from inside a page's form, and React hands a
          // submit up the components, portal or not: it is this form's alone.
          event.stopPropagation();
          const data = new FormData(event.currentTarget);
          let read = check(data);
          if (read.error === undefined && bounds) read = checkPartDates(data, bounds, index);
          setChecked(read.error !== undefined ? read : null);
          if (read.error === undefined) onSave(fieldsOf(data));
        }}
      >
        <ProblemProvider value={shown.problem}>
          <PartEditor
            options={options}
            prefix={SHEET_PREFIX}
            label={partLabel(index)}
            initial={initial}
            result={result?.(initial, index)}
            bounds={bounds}
          />
        </ProblemProvider>
        {shown.general && <ErrorNote message={shown.general} />}
        <div className="flex justify-end gap-2">
          <Button type="button" variant="outline" size="lg" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" size="lg" disabled={busy}>
            {submit}
          </Button>
        </div>
      </form>
    </Modal>
  );
}

/** "Add a part": what opens a new part's sheet, the same wherever one is made. */
export function AddPartButton({ onClick }: { onClick: () => void }) {
  return (
    <Button type="button" variant="outline" size="lg" onClick={onClick}>
      <Plus aria-hidden="true" data-icon="inline-start" />
      Add a part
    </Button>
  );
}

/** A part kept on a form until the form is sent; the key keeps React's cards apart. */
export type DraftPart = { key: number; fields: PartFields };

/**
 * The parts of a form that sends them with everything else — the New event
 * form and the log's — each made in its sheet and kept here as a card, as the
 * event's page shows a posted part, with a cog to edit or remove it. Each is
 * sent as hidden fields named under its place, `parts.N.…`, which is what
 * the form's reader reads. Removing one asks nothing: nothing has been sent.
 */
export function DraftParts({
  parts,
  onChange,
  options,
  check,
  describe,
  says,
  result,
  bounds,
}: {
  parts: DraftPart[];
  onChange: (parts: DraftPart[]) => void;
  options: EventOptions;
  /** Reads a part's sheet as the form will; `index` is its place, for the message. */
  check: (form: FormData, index: number) => Read<unknown>;
  /** The card's words for a part: how the page reads it. */
  describe: (fields: PartFields, index: number) => ReactNode;
  /** Said under the label, about every part. */
  says?: ReactNode;
  result?: PartResultFor;
  /** The event's dates as the form holds them when a sheet opens: its parts' live inside. */
  bounds?: () => PartBounds;
}) {
  // The part whose sheet is open, by its place: one past the last is a new one.
  const [open, setOpenAt] = useState<number | null>(null);
  // The event's dates as they were when it opened.
  const [within, setWithin] = useState<PartBounds | undefined>(undefined);
  const setOpen = (at: number | null) => {
    if (at !== null) setWithin(bounds?.());
    setOpenAt(at);
  };
  const adding = open === parts.length;
  const save = (fields: PartFields) => {
    if (open === null) return;
    const key = adding ? Math.max(-1, ...parts.map((part) => part.key)) + 1 : parts[open]!.key;
    onChange(adding ? [...parts, { key, fields }] : parts.map((part, at) => (at === open ? { key, fields } : part)));
    setOpen(null);
  };

  return (
    <section className="space-y-3" aria-label="Parts">
      <h2 className="section-label">Parts</h2>
      {says && <p className="text-sm text-muted-foreground">{says}</p>}
      {parts.length > 0 && (
        <ul className="space-y-4">
          {parts.map((part, index) => (
            <li key={part.key}>
              <Card>
                <CardHeader>
                  {describe(part.fields, index)}
                  <CardAction>
                    <CogMenu label={`Part ${partLabel(index)} menu`} compact>
                      {(close) => (
                        <>
                          <MenuButton
                            onClick={() => {
                              close();
                              setOpen(index);
                            }}
                          >
                            Edit part
                          </MenuButton>
                          <MenuButton
                            danger
                            onClick={() => {
                              close();
                              onChange(parts.filter((other) => other.key !== part.key));
                            }}
                          >
                            Remove part
                          </MenuButton>
                        </>
                      )}
                    </CogMenu>
                  </CardAction>
                </CardHeader>
                <DraftPartProblem index={index} />
              </Card>
              {part.fields.map(([name, value], at) => (
                <input key={`${name}-${at}`} type="hidden" name={`parts.${index}.${name}`} value={value} />
              ))}
            </li>
          ))}
        </ul>
      )}
      {parts.length < 30 && <AddPartButton onClick={() => setOpen(parts.length)} />}
      {open !== null && (
        <PartSheet
          // A fresh sheet for each part: it starts from what that part sent.
          key={adding ? "new" : parts[open]!.key}
          title={`${adding ? "New" : "Edit"} part ${partLabel(open)}`}
          submit={adding ? "Add part" : "Save part"}
          options={options}
          index={open}
          initial={adding ? undefined : parts[open]!.fields}
          check={(form) => check(form, open)}
          onSave={save}
          onClose={() => setOpen(null)}
          result={result}
          bounds={within}
        />
      )}
    </section>
  );
}

/**
 * What the api refused of a part kept as a card — its start or its deadline —
 * said under the card, since its boxes are hidden; nothing otherwise.
 */
function DraftPartProblem({ index }: { index: number }) {
  // Both read every time: hooks run in the same order on every draw.
  const starts = useProblem(`parts.${index}.startsAt`);
  const deadline = useProblem(`parts.${index}.deadline`);
  const problem = starts ?? deadline;
  if (!problem) return null;
  return (
    <CardContent>
      <FieldError>{problem}</FieldError>
    </CardContent>
  );
}

/**
 * What an event form holds of the event's dates, as wall times: the bounds of
 * the parts made beside it.
 */
export function boundsOf(form: HTMLFormElement | null, zone: string): PartBounds {
  const data = form ? new FormData(form) : null;
  if (!data) return { starts: "", deadline: "" };
  return { starts: wallOf(data, "startsAt") ?? "", deadline: readEventDates(data, zone).value?.ends ?? "" };
}

/**
 * A part as everybody reads it: its title, when it starts, how it is scored,
 * when its results are due — the api says whether that has passed — and
 * whether the box verifies them.
 */
export function PartSummary({
  part,
  index,
  zone,
}: {
  part: EventPart;
  index: number;
  zone: string;
}) {
  return (
    <div className="min-w-0 space-y-1">
      <p className="font-heading text-lg font-semibold leading-snug">
        {partLabel(index)}. {part.title}
      </p>
      <p className="text-sm text-muted-foreground">
        {part.summary}
        {part.scores[0]?.measure !== "none" && !part.hasLeaderboard && " · no leaderboard"}
        {!part.usesLevels && part.scores[0]?.measure !== "none" && " · no levels"}
        {part.startsAt && ` · ${formatMoment(part.startsAt, zone)}`}
        {part.deadline &&
          (part.deadlinePassed
            ? ` · deadline passed ${formatMoment(part.deadline, zone)}`
            : ` · results by ${formatMoment(part.deadline, zone)}`)}
        {part.verifiable && " · verified by the box"}
        {part.isTeam && " · done in teams"}
      </p>
      {part.description && <p className="whitespace-pre-line pt-1 text-sm">{part.description}</p>}
    </div>
  );
}
